import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

// The panel's drawn width (01.10 export #scenario_7, the Add Scenario button's own 126). Kept as a
// plain export for ScenarioRibbon.jsx's viewport clamp, as ScenarioMenu's MENU_WIDTH is.
export const ADD_MENU_WIDTH = 126

// Already added (letter 106, R5b): 8x5.5, 1px #4CBC18, round caps, ending 18 from the panel's right
// edge (01.10 export, S01:3757); the viewBox keeps the half-pixel stroke inside the box.
function AddedCheck() {
  return (
    <svg width="9" height="6.5" viewBox="-0.5 -0.5 9 6.5" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M0 2.39L2.85 5.5L8 0" stroke="#4CBC18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const ROW = 'w-full h-[26px] flex items-center gap-x-[8px] pl-[16px] pr-[17.5px] text-left text-[12px] text-brand-dark'
const ITEM = `${ROW} bg-white hover:bg-[#EEF3EC] focus-visible:bg-[#EEF3EC] outline-none`
const LOAD_ERROR_ID = 'add-scenario-load-error'

// Add Scenario's drop-down (letter 106, R5a; 01.10 export #scenario_7), portaled like ScenarioMenu.
// States, keyboard and closing: README.md, "Scenarios".
export default function AddScenarioMenu({ style, templates, loadFailed, onRetry, addedIds, onPick, onClose, anchorRef }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const inside = (target) => menuRef.current?.contains(target) || anchorRef?.current?.contains(target)
    const onPointerDown = (e) => {
      if (!inside(e.target)) onClose()
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose({ refocus: true })
    }
    // The list's own scroll (a long list) keeps it open; any other scroll or a resize moves the button.
    const onScroll = (e) => {
      if (!(e.target instanceof Node && menuRef.current?.contains(e.target))) onClose()
    }
    const onResize = () => onClose()
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [onClose, anchorRef])

  const items = () => [...(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? [])]

  // preventScroll, then the panel's own scrollTop: a page scroll would close the menu.
  const focusItem = (item) => {
    if (!item) return
    item.focus({ preventScroll: true })
    const menu = menuRef.current
    if (item.offsetTop < menu.scrollTop) menu.scrollTop = item.offsetTop
    else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight
    }
  }

  const hasItems = Boolean(templates?.length) || (templates === null && loadFailed)
  useEffect(() => {
    if (hasItems) focusItem(items()[0])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasItems])

  const handleKeyDown = (e) => {
    // Focus goes back to the button first, so the browser's own Tab moves on from there.
    if (e.key === 'Tab') return onClose({ refocus: true })
    const list = items()
    if (list.length === 0) return
    const index = list.indexOf(document.activeElement)
    let next = null
    if (e.key === 'ArrowDown') next = (index + 1) % list.length
    else if (e.key === 'ArrowUp') next = (index - 1 + list.length) % list.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = list.length - 1
    if (next === null) return
    e.preventDefault()
    focusItem(list[next])
  }

  const statusRow = (text) => (
    <li role="none" className="px-[16px] py-[5px] text-[12px] text-brand-unselectedGray">
      <span role="status">{text}</span>
    </li>
  )

  let body
  if (templates === null && loadFailed) {
    body = (
      <>
        <li role="none" id={LOAD_ERROR_ID} className="px-[16px] py-[5px] text-[12px] text-brand-statusSale">
          <span role="alert">We couldn't load your templates.</span>
        </li>
        <li role="none">
          <button type="button" role="menuitem" aria-describedby={LOAD_ERROR_ID} onClick={onRetry} className={ITEM}>
            Try again
          </button>
        </li>
      </>
    )
  } else if (templates === null) {
    body = statusRow('Loading…')
  } else if (templates.length === 0) {
    body = statusRow('No templates yet.')
  } else {
    body = templates.map((template) => {
      const added = addedIds.has(template._id)
      return (
        <li key={template._id} role="none">
          <button
            type="button"
            role="menuitem"
            title={template.template_name}
            // The check is drawn only; the name says it too.
            aria-label={added ? `${template.template_name}, already added` : template.template_name}
            onClick={() => onPick(template._id)}
            className={ITEM}
          >
            <span className="flex-1 min-w-0 truncate">{template.template_name}</span>
            {added ? <AddedCheck /> : null}
          </button>
        </li>
      )
    })
  }

  return createPortal(
    <ul
      ref={menuRef}
      role="menu"
      aria-label="Add a scenario from a template"
      aria-busy={templates === null && !loadFailed}
      onKeyDown={handleKeyDown}
      className="fixed z-30 w-[126px] max-h-[224px] overflow-y-auto rounded-[8px] bg-white py-[8px]"
      style={{ ...style, boxShadow: '0 0 40px rgba(115,147,94,0.15)', outline: '1px solid #C8D2C3', outlineOffset: '-1px' }}
    >
      {body}
    </ul>,
    document.getElementById('portal')
  )
}
